import React from 'react';
import { Popover, Tag, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import LevelChip, { type Level } from './LevelChip';

/** Role families share a colour so "who is an admin" reads without parsing the words. */
const roleColour = (role: string | null | undefined): string => {
  switch (String(role ?? '').toUpperCase()) {
    case 'SUPERADMIN':
      return 'red';
    case 'ADMIN':
      return 'gold';
    case 'VIEWER':
      return 'green';
    default:
      return 'purple';
  }
};

interface RoleBadgeProps {
  role: string | null | undefined;
  /** The level of the node the role is held on. Omitted when only the role name is known. */
  level?: Level;
  /** Shown in a tooltip rather than inline, so the badge stays one short token. */
  nodeName?: string;
}

/** `[level] ROLE`, with the node it applies to on hover. */
const RoleBadge: React.FC<RoleBadgeProps> = ({ role, level, nodeName }) => {
  const badge = (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {level && <LevelChip level={level} compact />}
      <Tag color={roleColour(role)} style={{ marginInlineEnd: 0 }}>
        {role ?? 'custom'}
      </Tag>
    </span>
  );
  return nodeName ? <Tooltip title={nodeName}>{badge}</Tooltip> : badge;
};

/** A help icon that explains which roles exist at each level and what ADMIN and VIEWER mean. */
export const RolesGlossary: React.FC = () => (
  <Popover
    title="Roles at each level"
    content={
      <div className="flex flex-col gap-1 max-w-[320px] text-sm">
        <div>Organisation: SUPERADMIN (organisation head, one seat), ADMIN, VIEWER.</div>
        <div>Entity: ADMIN, VIEWER.</div>
        <div>Workspace: ADMIN, ANALYST, CISO, DEV, VIEWER and custom roles.</div>
        <div>ADMIN: manages people and settings here and below.</div>
        <div>VIEWER: reads, changes nothing.</div>
      </div>
    }
  >
    <QuestionCircleOutlined
      className="ml-1 text-gray-400 cursor-help"
      aria-label="Roles at each level"
      onClick={event => event.stopPropagation()}
    />
  </Popover>
);

export default RoleBadge;
